import { useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import {
  AutocompleteProps,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  getHazardEventIcon,
  getLocationIcon,
  getOpacityForGeoEventMarker,
  getWildfireGeoEventIcon,
} from 'features/mapping/icons';
import { getFloodingEventIcon } from 'features/mapping/icons/iconData/floodingIcons';
import { LatLng } from 'shared/types';
import { getHighlightedSearchMatch } from 'hooks/useMapSearch';
import {
  isWildfireGeoEvent,
  isFloodingGeoEvent,
  isHazardGeoEvent,
  isLocationGeoEvent,
} from 'features/geoEvents/geoEvent.utils';
import { GeoEventType } from 'features/geoEvents/geoEvent.types';
import { useLocationV2Enabled } from 'features/locations/hooks/useLocationV2Enabled';
import { fetchGeoEventsForAutocomplete } from './useGeoEventAutocomplete.queries';

// Generic constraint for any geo event with minimal required properties
type BaseGeoEvent = {
  id: number;
  name: string;
  address?: string | null;
};

export type UseGeoEventAutocomplete<T extends BaseGeoEvent> = {
  muiAutocompleteProps: Omit<
    AutocompleteProps<T, boolean, false, false>,
    'renderInput'
  >;
};

export type UseGeoEventAutocompleteProps<T extends BaseGeoEvent> = {
  enabled: boolean;
  filterGeoEvents?: (geoEvents: T[]) => T[];
  latLng?: LatLng;
  value: T[] | T | null;
  geoEventTypes: GeoEventType[];
  multiple?: boolean;
  reporterManagedOnly?: boolean;
};

export const useGeoEventAutocomplete = <T extends BaseGeoEvent>(
  props: UseGeoEventAutocompleteProps<T>,
): UseGeoEventAutocomplete<T> => {
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounceValue(query, 500);

  const {
    enabled,
    filterGeoEvents,
    latLng,
    value,
    geoEventTypes,
    multiple = true,
    reporterManagedOnly,
  } = props;

  const showLocationV2 = useLocationV2Enabled();

  const getIcon = (option: T): { data: string } => {
    if (isWildfireGeoEvent(option)) {
      return getWildfireGeoEventIcon(option);
    }
    if (isFloodingGeoEvent(option)) {
      return getFloodingEventIcon(option);
    }
    if (isHazardGeoEvent(option)) {
      return getHazardEventIcon(option);
    }
    if (isLocationGeoEvent(option)) {
      return getLocationIcon(option.data.locationType, showLocationV2);
    }
    return { data: '' };
  };

  const renderOption: AutocompleteProps<
    T,
    boolean,
    false,
    false
  >['renderOption'] = (optionProps, option, { inputValue }) => {
    const icon = getIcon(option);
    const { key, ...listItemProps } = optionProps;

    return (
      <ListItem key={key} {...listItemProps}>
        <ListItemIcon sx={{ minWidth: 36 }}>
          <img
            src={icon.data}
            alt=""
            width={24}
            style={{
              opacity: getOpacityForGeoEventMarker({
                geoEvent: option as unknown as Partial<T>,
                isFadable: true,
              }),
            }}
          />
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography>
              {getHighlightedSearchMatch(inputValue, option.name)}
            </Typography>
          }
          secondary={option.address}
        />
      </ListItem>
    );
  };

  const { data: geoEvents, isFetching } = useQuery({
    queryKey: [
      'search_geo_events',
      geoEventTypes,
      debouncedQuery,
      latLng?.lat,
      latLng?.lng,
      reporterManagedOnly,
    ],
    queryFn: async () => {
      const geoEventResults = (await fetchGeoEventsForAutocomplete(
        debouncedQuery,
        latLng,
        geoEventTypes,
        reporterManagedOnly,
      )) as unknown as T[];

      if (filterGeoEvents) {
        return filterGeoEvents(geoEventResults);
      }

      return geoEventResults;
    },
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    enabled,
  });

  let selectedIds: number[];
  if (Array.isArray(value)) {
    selectedIds = value.map((geoEvent) => geoEvent.id);
  } else {
    selectedIds = value ? [value.id] : [];
  }

  // Why is the optional chaining used here on `filter?.()`?
  // https://linear.app/watchduty/issue/ENG-3079/crash-when-editing-an-incident
  const geoEventOptionsWithoutSelected =
    geoEvents?.filter?.((ge) => !selectedIds.includes(ge.id)) || [];

  return {
    muiAutocompleteProps: {
      multiple,
      options: geoEventOptionsWithoutSelected,
      getOptionLabel: (geoEvent: T) => geoEvent.name,
      getOptionKey: (geoEvent: T) => geoEvent.id,
      isOptionEqualToValue: (option: T, val: T) => option.id === val.id,
      loading: isFetching,
      renderOption,
      filterOptions: (opt) => opt,
      onInputChange: (_, entry, reason) => {
        //  If they've performed an action other than entering input, we'll reset the query since most
        //  of these actions result in the entry being reset and we want the results to match that state
        setQuery(reason === 'input' ? entry : '');
      },
    },
  };
};
